import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Search, Loader2, Building2, ChevronDown, Home, X, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATES = [
  { key: "all", label: "All" },
  { key: "missing_developer", label: "Missing Developer" },
  { key: "has_developer_missing_project", label: "Has Developer / Missing Project" },
  { key: "tagged", label: "Fully Tagged" },
];

// Canonical-only searchable picker (no candidate creation). /developers & /projects return CANONICAL only.
const CanonPicker = ({ kind, value, onChange, developerId, disabled, testid }) => {
  const isDev = kind === "developer";
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { q: q.trim() };
        if (!isDev && developerId) params.developer_id = developerId;
        const { data } = await api.get(isDev ? "/developers" : "/projects", { params });
        setOpts(data || []);
      } catch { setOpts([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open, developerId, isDev]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const awaiting = !isDev && !developerId;
  return (
    <div ref={box} className="relative" data-testid={testid}>
      <button type="button" data-testid={`${testid}-trigger`} disabled={disabled || awaiting}
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-left text-sm disabled:bg-slate-50 disabled:text-slate-400">
        <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
        <span className={`truncate ${value?.name ? "text-slate-800 font-medium" : "text-slate-400"}`}>
          {value?.name || (awaiting ? "Select a developer first" : isDev ? "Select canonical developer" : "Select canonical project")}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400 ml-auto shrink-0" />
      </button>
      {open && !awaiting && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              data-testid={`${testid}-search`} className="w-full text-sm outline-none" />
          </div>
          <ul className="max-h-56 overflow-auto py-1">
            <li><button type="button" data-testid={`${testid}-clear`} onClick={() => { onChange({ id: null, name: null }); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50">— Clear —</button></li>
            {loading && <li className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</li>}
            {!loading && opts.map((o) => (
              <li key={o.id}><button type="button" data-testid={`${testid}-option-${o.id}`}
                onClick={() => { onChange({ id: o.id, name: o.name }); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between">
                <span>{o.name}</span>{value?.id === o.id && <Check className="h-4 w-4 text-primary" />}</button></li>
            ))}
            {!loading && opts.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">No canonical matches.</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function AdminListingTagging() {
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState("missing_developer");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // { row, dev, proj }
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/admin/listings/tagging/summary"); setSummary(data); } catch {}
  }, []);
  const loadRows = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/listings/tagging", { params: { state, q: q.trim(), limit: 50 } }); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [state, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const openEdit = (row) => setEditing({
    row,
    dev: { id: row.developer_id, name: row.developer_name },
    proj: { id: row.project_id, name: row.project_name },
  });
  const onPickDev = (v) => setEditing((e) => ({ ...e, dev: v, proj: { id: null, name: null } }));
  const onPickProj = (v) => setEditing((e) => ({ ...e, proj: v }));

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/properties/${editing.row.id}/canonical-tags`, {
        developer_id: editing.dev?.id || null, project_id: editing.proj?.id || null,
      });
      toast.success("Canonical tags updated");
      setEditing(null); loadRows(); loadSummary();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-tagging" className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Developer / Project Tagging</h1>
        <p className="text-sm text-slate-500 mt-1">Assign canonical Developer and Project / Development to existing listings. Canonical records only — no auto-mapping.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tagging-summary">
          {[["total", "Listings"], ["missing_developer", "Missing Developer"], ["has_developer_missing_project", "Dev / No Project"], ["fully_tagged", "Fully Tagged"]].map(([k, l]) => (
            <div key={k} className="bg-card border border-border rounded-xl p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div>
              <div className="font-display text-xl font-bold text-slate-900 mt-0.5">{summary[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {STATES.map((s) => (
          <button key={s.key} data-testid={`tagging-filter-${s.key}`} onClick={() => setState(s.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${state === s.key ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{s.label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-9 bg-white">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title…" data-testid="tagging-search" className="text-sm outline-none w-44" />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left px-4 py-2.5">Listing</th><th className="text-left px-4 py-2.5">Developer</th><th className="text-left px-4 py-2.5">Project / Development</th><th className="px-4 py-2.5"></th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No listings for this filter.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`tagging-row-${r.id}`} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800 flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-slate-400" />{r.title || "(untitled)"}</div>
                  <div className="text-xs text-slate-400">{r.listing_type} · {r.city || "—"} · {r.status}</div>
                </td>
                <td className="px-4 py-3">{r.developer_name ? <span className="text-slate-700">{r.developer_name}</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3">{r.project_name ? <span className="text-slate-700">{r.project_name}</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" data-testid={`tagging-edit-${r.id}`} onClick={() => openEdit(r)}>Edit tags</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="tagging-dialog">
          <DialogHeader>
            <DialogTitle>Canonical tags</DialogTitle>
            <DialogDescription className="truncate">{editing?.row?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500">Developer</label>
              <div className="mt-1.5"><CanonPicker kind="developer" testid="tagging-developer" value={editing?.dev} onChange={onPickDev} /></div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Project / Development</label>
              <div className="mt-1.5"><CanonPicker kind="project" testid="tagging-project" developerId={editing?.dev?.id} value={editing?.proj} onChange={onPickProj} /></div>
              {!editing?.dev?.id && <p className="text-[11px] text-slate-400 mt-1">Select a developer to enable project selection.</p>}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button data-testid="tagging-save" onClick={save} disabled={saving} className="bg-[#0F2C59] text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              <Button variant="outline" data-testid="tagging-clear" onClick={() => setEditing((e) => ({ ...e, dev: { id: null, name: null }, proj: { id: null, name: null } }))}>Clear both</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
